import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, FloppyDisk, Warning, ChartBar, Clock, Users, ArrowsCounterClockwise, CalendarPlus, X } from '@phosphor-icons/react';
import { ProjectRecapsSection } from './ProjectRecapsSection';
import { ProjectGuaranteeSection } from './ProjectGuaranteeSection';
import { ProjectRentalsSection } from './ProjectRentalsSection';
import LaborQuotasSection from './LaborQuotasSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TVA_CHOICES = [0, 5.5, 10, 20];
const HOURLY_DIVISOR = 7; // hours per day for daily_rate -> hourly_rate (matches backend)

const emptyLine = () => ({ id: '', category: '', description: '', amount_ht: 0, tva_rate: 20 });

export const BudgetTab = ({ project }) => {
  const [data, setData] = useState(null);
  const [lines, setLines] = useState([]);
  const [quota, setQuota] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workforce, setWorkforce] = useState([]); // {user_id, name, hourly_rate, hours_approved, ...}
  const [splitMap, setSplitMap] = useState({}); // user_id -> hours assigned in forecast (manual override)
  const [expenseModal, setExpenseModal] = useState({ open: false, defaultType: 'forecast', defaultLineId: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [budgetRes, wfRes] = await Promise.all([
        axios.get(`${API_URL}/api/projects/${project.id}/budget`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects/${project.id}/workforce`, { withCredentials: true }).catch(() => ({ data: { rows: [] } })),
      ]);
      setData(budgetRes.data);
      setLines(budgetRes.data.lines || []);
      setQuota(budgetRes.data.labor_hours_quota || 0);
      // Keep only employees explicitly assigned to the project for the forecast
      const assigned = (wfRes.data?.rows || []).filter(r => r.is_assigned);
      setWorkforce(assigned);
    } catch (e) {
      toast.error('Erreur chargement gestion');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (project?.id) load(); /* eslint-disable-next-line */ }, [project?.id]);

  const deleteRealExpense = async (expId) => {
    if (!window.confirm('Supprimer cette depense reelle ?')) return;
    try {
      await axios.delete(`${API_URL}/api/projects/${project.id}/expenses/${expId}`, { withCredentials: true });
      toast.success('Depense supprimee');
      load();
    } catch { toast.error('Erreur'); }
  };

  const handleSubmitExpense = async ({ type, payload }) => {
    if (type === 'forecast') {
      // Add a new forecast line locally; user must hit "Enregistrer la gestion" to persist.
      setLines(prev => [...prev, {
        id: '', category: payload.category || '', description: payload.description,
        amount_ht: payload.amount_ht, tva_rate: payload.tva_rate,
      }]);
      toast.success('Ligne previsionnelle ajoutee. Cliquez "Enregistrer la gestion" pour sauvegarder.');
    } else {
      // Real expense: persisted immediately via existing endpoint.
      try {
        await axios.post(`${API_URL}/api/projects/${project.id}/expenses`, {
          project_id: project.id,
          category: payload.category,
          description: payload.description,
          amount_ht: payload.amount_ht,
          tva_rate: payload.tva_rate,
          date: payload.date,
          hours: payload.hours || null,
          budget_line_id: payload.budget_line_id || null,
        }, { withCredentials: true });
        toast.success('Depense reelle enregistree');
        load();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Erreur');
      }
    }
    setExpenseModal({ open: false, defaultType: 'forecast', defaultLineId: '' });
  };

  const getCategoryLabelFallback = (key) => {
    const map = { materials: 'Achat / Materiaux', fuel: 'Carburant', subcontractor: 'Sous-traitant', rental: 'Location', logistics: 'Logistique', other: 'Autre' };
    return map[key] || key || '-';
  };

  const addLine = () => setLines([...lines, emptyLine()]);
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));
  const updateLine = (idx, patch) => setLines(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanLines = lines.filter(l => l.description.trim()).map(l => ({
        id: l.id || undefined,
        category: l.category || '',
        description: l.description.trim(),
        amount_ht: parseFloat(l.amount_ht || 0),
        tva_rate: parseFloat(l.tva_rate || 20),
      }));
      const res = await axios.put(`${API_URL}/api/projects/${project.id}/budget`, {
        labor_hours_quota: parseFloat(quota || 0),
        lines: cleanLines,
      }, { withCredentials: true });
      toast.success('Gestion enregistree');
      setData(res.data);
      setLines(res.data.lines || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: '24px', color: '#5E5F66' }}>Chargement...</div>;

  const totalBudget = lines.reduce((s, l) => s + parseFloat(l.amount_ht || 0), 0);
  const pctExpenses = totalBudget > 0 ? Math.round((data.total_expenses_ht / totalBudget) * 1000) / 10 : 0;
  const pctHours = quota > 0 ? Math.round((data.total_hours / quota) * 1000) / 10 : 0;
  const alertExp = totalBudget > 0 && pctExpenses >= 95;
  const alertHours = quota > 0 && pctHours >= 95;

  // Forecast labor cost from the splitMap
  const forecastLaborCost = workforce.reduce((s, w) => {
    const h = splitMap[w.user_id] !== undefined ? Number(splitMap[w.user_id]) : 0;
    return s + h * (w.hourly_rate || 0);
  }, 0);

  // Margin computations (HT)
  const revenueQuotes = data.total_quotes_ht || 0;
  const revenueInvoices = data.total_invoices_ht || 0;
  const realLaborCost = data.labor_cost_real || 0;
  const marginForecast = revenueQuotes - totalBudget - forecastLaborCost;
  const marginReal = revenueInvoices - data.total_expenses_ht - realLaborCost;
  const fmt = (n) => `${(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;
  const pct = (margin, base) => base > 0 ? `${Math.round((margin / base) * 1000) / 10}%` : '-';

  const cardStyle = (alert) => ({
    background: alert ? '#FEECEB' : 'white',
    border: `2px solid ${alert ? '#C62828' : '#E2E2E6'}`,
    borderRadius: '10px',
    padding: '18px 20px',
    flex: 1,
    minWidth: '280px',
  });

  return (
    <div data-testid="budget-tab">
      {/* Garantie / Caution / Bon de commande / Prorata */}
      <ProjectGuaranteeSection project={project} onChange={load} />

      {/* Locations machines / equipement */}
      <ProjectRentalsSection project={project} onChange={load} />

      {/* KPI cards */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <div style={cardStyle(alertExp)} data-testid="kpi-expenses">
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px', color: alertExp ? '#C62828' : '#5E5F66', fontWeight: '700' }}>
            <ChartBar size={14} /> Depenses HT {alertExp && <Warning size={16} weight="fill" />}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '6px' }}>
            <div style={{ fontSize: '1.6rem', fontWeight: '800', color: alertExp ? '#C62828' : '#002FA7' }}>{data.total_expenses_ht.toFixed(2)} EUR</div>
            <div style={{ fontSize: '0.9rem', color: '#5E5F66' }}>/ {totalBudget.toFixed(2)} EUR</div>
          </div>
          <div style={{ height: '8px', background: '#F4F5F7', borderRadius: '4px', marginTop: '10px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(pctExpenses, 100)}%`, background: alertExp ? '#C62828' : (pctExpenses >= 75 ? '#E65100' : '#2E7D32'), transition: 'width 0.3s' }} />
          </div>
          <div style={{ marginTop: '4px', fontSize: '0.85rem', fontWeight: '600', color: alertExp ? '#C62828' : '#5E5F66' }}>{pctExpenses}% consommé {alertExp && '⚠ seuil 95% dépassé'}</div>
        </div>

        <div style={cardStyle(alertHours)} data-testid="kpi-hours">
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px', color: alertHours ? '#C62828' : '#5E5F66', fontWeight: '700' }}>
            <Clock size={14} /> Heures MO {alertHours && <Warning size={16} weight="fill" />}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '6px' }}>
            <div style={{ fontSize: '1.6rem', fontWeight: '800', color: alertHours ? '#C62828' : '#002FA7' }}>{data.total_hours.toFixed(1)}h</div>
            <div style={{ fontSize: '0.9rem', color: '#5E5F66' }}>/ {parseFloat(quota || 0).toFixed(1)}h</div>
          </div>
          <div style={{ height: '8px', background: '#F4F5F7', borderRadius: '4px', marginTop: '10px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(pctHours, 100)}%`, background: alertHours ? '#C62828' : (pctHours >= 75 ? '#E65100' : '#2E7D32'), transition: 'width 0.3s' }} />
          </div>
          <div style={{ marginTop: '4px', fontSize: '0.85rem', fontWeight: '600', color: alertHours ? '#C62828' : '#5E5F66' }}>{pctHours}% consommé {alertHours && '⚠ seuil 95% dépassé'}</div>
        </div>
      </div>

      {/* Marge previsionnelle vs reelle */}
      <div className="card" style={{ padding: '14px 18px', marginBottom: '18px' }} data-testid="margin-block">
        <h3 style={{ margin: '0 0 10px 0', fontSize: '0.95rem' }}>Marges du chantier (HT)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          {/* Forecast margin */}
          <div style={{ padding: '12px 14px', background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 8 }} data-testid="margin-forecast">
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.4, color: '#0369A1', fontWeight: 700, marginBottom: 6 }}>Marge previsionnelle</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: marginForecast >= 0 ? '#0369A1' : '#C62828' }}>
              {fmt(marginForecast)}
              <span style={{ fontSize: '0.85rem', fontWeight: 600, marginLeft: 8, color: marginForecast >= 0 ? '#0369A1' : '#C62828' }}>{pct(marginForecast, revenueQuotes)}</span>
            </div>
            <div style={{ fontSize: '0.72rem', color: '#5E5F66', marginTop: 8, lineHeight: 1.55 }}>
              Recettes (devis) : <strong>{fmt(revenueQuotes)}</strong><br />
              − Depenses prev. : <strong>{fmt(totalBudget)}</strong><br />
              − Cout MO prev. : <strong>{fmt(forecastLaborCost)}</strong>
            </div>
          </div>
          {/* Real margin */}
          <div style={{ padding: '12px 14px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8 }} data-testid="margin-real">
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.4, color: '#166534', fontWeight: 700, marginBottom: 6 }}>Marge reelle</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: marginReal >= 0 ? '#166534' : '#C62828' }}>
              {fmt(marginReal)}
              <span style={{ fontSize: '0.85rem', fontWeight: 600, marginLeft: 8, color: marginReal >= 0 ? '#166534' : '#C62828' }}>{pct(marginReal, revenueInvoices)}</span>
            </div>
            <div style={{ fontSize: '0.72rem', color: '#5E5F66', marginTop: 8, lineHeight: 1.55 }}>
              Recettes (factures) : <strong>{fmt(revenueInvoices)}</strong><br />
              − Depenses reelles : <strong>{fmt(data.total_expenses_ht)}</strong><br />
              − Cout MO reel : <strong>{fmt(realLaborCost)}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Hours quota input */}
      <div className="card" style={{ padding: '14px 18px', marginBottom: '18px' }}>
        <label className="form-label" style={{ marginBottom: '6px' }}>Quota d'heures MO prévisionnel (global, manuel)</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '280px' }}>
          <input type="number" min="0" step="1" className="form-input" value={quota} data-testid="labor-quota-input"
                 onChange={(e) => setQuota(e.target.value)} />
          <span style={{ color: '#5E5F66', fontSize: '0.9rem' }}>heures</span>
        </div>
      </div>

      {/* Auto-computed per-category quotas (from accepted quotes + amendments) */}
      <LaborQuotasSection project={project} />

      {/* Workforce forecast — visible only when a quota is set */}
      <ForecastBySalarie
        quota={parseFloat(quota || 0)}
        workforce={workforce}
        splitMap={splitMap}
        setSplitMap={setSplitMap}
        project={project}
      />

      {/* Budget lines */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>Depenses du chantier</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-primary btn-sm" onClick={() => setExpenseModal({ open: true })} data-testid="open-add-expense-btn">
            <Plus size={14} weight="bold" /> Ajouter une depense
          </button>
        </div>
      </div>

      <div className="card" style={{ overflow: 'auto', marginBottom: '16px' }}>
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Categorie</th>
              <th>Description</th>
              <th style={{ width: '130px', textAlign: 'right' }}>Montant prev. HT</th>
              <th style={{ width: '130px', textAlign: 'right' }}>Reel cumule HT</th>
              <th style={{ width: '90px', textAlign: 'right' }}>Ecart</th>
              <th style={{ width: '100px', textAlign: 'right' }}>TVA</th>
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: '24px', color: '#5E5F66' }}>Aucune ligne previsionnelle. Ajoutez une depense pour commencer.</td></tr>
            )}
            {lines.map((ln, idx) => {
              const realLines = ln.real_expenses || [];
              const realTotal = ln.real_total_ht || 0;
              const ecart = parseFloat(ln.amount_ht || 0) - realTotal;
              const overflow = realTotal > parseFloat(ln.amount_ht || 0) && parseFloat(ln.amount_ht || 0) > 0;
              return (
                <React.Fragment key={ln.id || idx}>
                  <tr data-testid={`budget-line-${idx}`} style={{ backgroundColor: '#F0F9FF' }}>
                    <td><input className="form-input" placeholder="ex: Gros oeuvre" value={ln.category} onChange={(e) => updateLine(idx, { category: e.target.value })} style={{ padding: '6px 8px' }} /></td>
                    <td><input className="form-input" placeholder="Description *" value={ln.description} onChange={(e) => updateLine(idx, { description: e.target.value })} style={{ padding: '6px 8px' }} /></td>
                    <td><input type="number" min="0" step="0.01" className="form-input" value={ln.amount_ht} onChange={(e) => updateLine(idx, { amount_ht: e.target.value })} style={{ padding: '6px 8px', textAlign: 'right' }} /></td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: overflow ? '#C62828' : '#0369A1' }}>{realTotal.toFixed(2)} EUR</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: overflow ? '#C62828' : (ecart >= 0 ? '#166534' : '#C62828') }}>{ecart.toFixed(2)} EUR</td>
                    <td>
                      <select className="form-input" value={ln.tva_rate} onChange={(e) => updateLine(idx, { tva_rate: parseFloat(e.target.value) })} style={{ padding: '6px 8px' }}>
                        {TVA_CHOICES.map((v) => <option key={v} value={v}>{v}%</option>)}
                      </select>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => setExpenseModal({ open: true, defaultType: 'real', defaultLineId: ln.id })} title="Saisir une depense reelle pour cette ligne" data-testid={`add-real-for-${idx}`}>
                          <Plus size={11} />
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => removeLine(idx)}><Trash size={13} /></button>
                      </div>
                    </td>
                  </tr>
                  {realLines.map((re) => (
                    <tr key={re.id} style={{ backgroundColor: '#FFFFFF' }} data-testid={`real-expense-${re.id}`}>
                      <td style={{ paddingLeft: 24, fontSize: '0.78rem', color: '#6B7280' }}>↳ {getCategoryLabelFallback(re.category)}</td>
                      <td style={{ fontSize: '0.82rem' }}>
                        <span style={{ color: '#1E2030' }}>{re.description}</span>
                        <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#9CA3AF' }}>{re.date}</span>
                      </td>
                      <td></td>
                      <td style={{ textAlign: 'right', fontSize: '0.82rem', fontWeight: 600, color: '#0369A1' }}>{re.amount_ht.toFixed(2)} EUR</td>
                      <td></td>
                      <td style={{ textAlign: 'right', fontSize: '0.78rem', color: '#9CA3AF' }}>{re.tva_rate}%</td>
                      <td>
                        <button className="btn btn-danger btn-sm" onClick={() => deleteRealExpense(re.id)} title="Supprimer" data-testid={`del-real-${re.id}`}>
                          <Trash size={11} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="2" style={{ textAlign: 'right', fontWeight: '700', padding: '10px 12px' }}>Total previsionnel HT</td>
              <td style={{ textAlign: 'right', fontWeight: '800', fontSize: '1.05rem', padding: '10px 12px' }}>{totalBudget.toFixed(2)} EUR</td>
              <td style={{ textAlign: 'right', fontWeight: '800', fontSize: '1.05rem', padding: '10px 12px', color: '#0369A1' }}>{(data.total_expenses_ht || 0).toFixed(2)} EUR</td>
              <td colSpan="3"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Real expenses NOT linked to any forecast line */}
      {(data.unlinked_expenses || []).length > 0 && (
        <div className="card" style={{ marginBottom: 16, padding: 14 }} data-testid="unlinked-expenses">
          <h4 style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#92400E' }}>Depenses reelles non liees a une ligne previsionnelle</h4>
          <table className="data-table" style={{ fontSize: '0.85rem' }}>
            <thead><tr><th>Date</th><th>Categorie</th><th>Description</th><th style={{ textAlign: 'right' }}>HT</th><th></th></tr></thead>
            <tbody>
              {data.unlinked_expenses.map(re => (
                <tr key={re.id}>
                  <td>{re.date}</td>
                  <td>{getCategoryLabelFallback(re.category)}</td>
                  <td>{re.description}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{re.amount_ht.toFixed(2)} EUR</td>
                  <td>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteRealExpense(re.id)} title="Supprimer">
                      <Trash size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving} data-testid="save-budget-btn">
          <FloppyDisk size={16} /> {saving ? 'Enregistrement...' : 'Enregistrer la gestion'}
        </button>
      </div>

      {expenseModal.open && (
        <AddExpenseModal
          defaultType={expenseModal.defaultType || 'forecast'}
          defaultLineId={expenseModal.defaultLineId || ''}
          forecastLines={lines}
          onClose={() => setExpenseModal({ open: false, defaultType: 'forecast', defaultLineId: '' })}
          onSubmit={handleSubmitExpense}
        />
      )}
      <ProjectRecapsSection project={project} />
    </div>
  );
};

export default BudgetTab;

// --- Unified Add Expense modal: ask forecast or real, then capture fields ---
const EXPENSE_CATEGORIES = [
  { value: 'materials', label: 'Achat / Materiaux' },
  { value: 'fuel', label: 'Carburant' },
  { value: 'subcontractor', label: 'Sous-traitant' },
  { value: 'rental', label: 'Location' },
  { value: 'logistics', label: 'Logistique' },
  { value: 'other', label: 'Autre' },
];

const AddExpenseModal = ({ defaultType, defaultLineId, forecastLines, onClose, onSubmit }) => {
  const [type, setType] = useState(defaultType || 'forecast');
  const todayStr = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    category: 'materials',
    description: '',
    amount_ht: '',
    tva_rate: 20,
    date: todayStr,
    hours: '',
    budget_line_id: defaultLineId || '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error('Description requise'); return; }
    const amt = parseFloat(form.amount_ht);
    if (!(amt > 0)) { toast.error('Montant HT requis'); return; }
    setSaving(true);
    try {
      await onSubmit({
        type,
        payload: {
          category: form.category,
          description: form.description.trim(),
          amount_ht: amt,
          tva_rate: parseFloat(form.tva_rate) || 20,
          date: form.date,
          hours: form.hours ? parseFloat(form.hours) : null,
          budget_line_id: type === 'real' ? (form.budget_line_id || null) : null,
        },
      });
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} data-testid="add-expense-modal">
      <div style={{ background: 'white', borderRadius: 10, width: '95%', maxWidth: 540, padding: 22, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Plus size={18} weight="bold" /> Ajouter une depense
          </h3>
          <button onClick={onClose} disabled={saving} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>

        {/* Type selector — pivotal choice asked by user */}
        <div className="form-group">
          <label className="form-label" style={{ marginBottom: 8 }}>Type de depense *</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <label style={{ flex: 1, padding: '12px 14px', border: '2px solid', borderColor: type === 'forecast' ? '#0369A1' : '#D1D5DB', borderRadius: 8, cursor: 'pointer', backgroundColor: type === 'forecast' ? '#F0F9FF' : 'white' }}>
              <input type="radio" name="exp-type" value="forecast" checked={type === 'forecast'} onChange={() => setType('forecast')} data-testid="exp-type-forecast" style={{ marginRight: 8 }} />
              <strong>Previsionnel</strong>
              <div style={{ fontSize: '0.72rem', color: '#5E5F66', marginTop: 2 }}>Ajoute une ligne au budget previsionnel</div>
            </label>
            <label style={{ flex: 1, padding: '12px 14px', border: '2px solid', borderColor: type === 'real' ? '#166534' : '#D1D5DB', borderRadius: 8, cursor: 'pointer', backgroundColor: type === 'real' ? '#F0FDF4' : 'white' }}>
              <input type="radio" name="exp-type" value="real" checked={type === 'real'} onChange={() => setType('real')} data-testid="exp-type-real" style={{ marginRight: 8 }} />
              <strong>Reelle</strong>
              <div style={{ fontSize: '0.72rem', color: '#5E5F66', marginTop: 2 }}>Saisit une depense reellement engagee</div>
            </label>
          </div>
        </div>

        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">Categorie *</label>
            <select required className="form-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="exp-category">
              {EXPENSE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Description *</label>
            <input type="text" required className="form-input" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="exp-description" placeholder="ex: Achat ciment" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="form-group">
              <label className="form-label">Montant HT (EUR) *</label>
              <input type="number" min="0" step="0.01" required className="form-input" value={form.amount_ht}
                onChange={(e) => setForm({ ...form, amount_ht: e.target.value })} data-testid="exp-amount" />
            </div>
            <div className="form-group">
              <label className="form-label">TVA %</label>
              <select className="form-input" value={form.tva_rate}
                onChange={(e) => setForm({ ...form, tva_rate: parseFloat(e.target.value) })} data-testid="exp-tva">
                {[0, 5.5, 10, 20].map(v => <option key={v} value={v}>{v}%</option>)}
              </select>
            </div>
          </div>

          {/* Real-only fields */}
          {type === 'real' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input type="date" required className="form-input" value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="exp-date" />
                </div>
                <div className="form-group">
                  <label className="form-label">Heures (opt.)</label>
                  <input type="number" min="0" step="0.5" className="form-input" value={form.hours}
                    onChange={(e) => setForm({ ...form, hours: e.target.value })} data-testid="exp-hours" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Lier a une ligne previsionnelle (optionnel)</label>
                <select className="form-input" value={form.budget_line_id}
                  onChange={(e) => setForm({ ...form, budget_line_id: e.target.value })} data-testid="exp-link-line">
                  <option value="">-- Non liee --</option>
                  {(forecastLines || []).filter(ln => ln.id && ln.description).map(ln => (
                    <option key={ln.id} value={ln.id}>{ln.description} ({parseFloat(ln.amount_ht || 0).toFixed(2)} EUR)</option>
                  ))}
                </select>
                <div style={{ fontSize: '0.72rem', color: '#9CA3AF', marginTop: 2 }}>
                  Une depense liee s'affiche sous sa ligne previsionnelle pour suivre l'ecart.
                </div>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="exp-save">
              <Plus size={14} /> {saving ? 'Enregistrement...' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Forecast block: displayed when quota > 0 and at least 1 employee is assigned ---
const fmtMoney = (n) => `${(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;
const fmtHours = (n) => `${(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h`;

const ForecastBySalarie = ({ quota, workforce, splitMap, setSplitMap, project }) => {
  const assignedCount = workforce.length;

  // Default equal split when quota or assigned employees change AND user hasn't customized
  useEffect(() => {
    if (assignedCount === 0 || quota <= 0) return;
    const hasCustom = Object.keys(splitMap).length > 0 && workforce.some(w => splitMap[w.user_id] !== undefined);
    if (hasCustom) return;
    const equalShare = Math.round((quota / assignedCount) * 10) / 10;
    const next = {};
    workforce.forEach((w, idx) => {
      // Distribute remainder to last to keep exact total
      if (idx === workforce.length - 1) {
        const sum = (workforce.length - 1) * equalShare;
        next[w.user_id] = Math.round((quota - sum) * 10) / 10;
      } else {
        next[w.user_id] = equalShare;
      }
    });
    setSplitMap(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quota, assignedCount]);

  const rows = useMemo(() => workforce.map(w => {
    const h = splitMap[w.user_id] !== undefined ? Number(splitMap[w.user_id]) : 0;
    const cost = h * (w.hourly_rate || 0);
    return { ...w, forecast_hours: h, forecast_cost: cost };
  }), [workforce, splitMap]);

  const totalForecastHours = rows.reduce((s, r) => s + (r.forecast_hours || 0), 0);
  const totalForecastCost = rows.reduce((s, r) => s + (r.forecast_cost || 0), 0);
  const remaining = quota - totalForecastHours;
  const overflow = Math.abs(remaining) > 0.05;
  const [genOpen, setGenOpen] = useState(false);

  const resetEqual = () => {
    if (assignedCount === 0 || quota <= 0) return;
    const equal = Math.round((quota / assignedCount) * 10) / 10;
    const next = {};
    workforce.forEach((w, idx) => {
      next[w.user_id] = idx === workforce.length - 1
        ? Math.round((quota - equal * (workforce.length - 1)) * 10) / 10
        : equal;
    });
    setSplitMap(next);
  };

  if (quota <= 0) {
    return (
      <div className="card" style={{ padding: '14px 18px', marginBottom: '18px', backgroundColor: '#FAFBFC', border: '1px dashed #D1D5DB' }} data-testid="forecast-empty">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6B7280', fontSize: '0.85rem' }}>
          <Users size={16} /> Renseignez un quota d'heures ci-dessus pour generer le previsionnel par salarie.
        </div>
      </div>
    );
  }

  if (assignedCount === 0) {
    return (
      <div className="card" style={{ padding: '14px 18px', marginBottom: '18px', backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }} data-testid="forecast-no-employees">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#92400E', fontSize: '0.85rem' }}>
          <Warning size={16} weight="fill" /> Aucun salarie assigne a ce chantier. Assignez-les depuis l'onglet "Equipe" pour voir la repartition previsionnelle.
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: '16px 18px', marginBottom: '18px' }} data-testid="forecast-block">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Users size={16} weight="bold" /> Previsionnel main d'oeuvre par salarie
        </h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={resetEqual} title="Repartition equitable" data-testid="forecast-reset-equal">
            <ArrowsCounterClockwise size={12} /> Repartition egale
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setGenOpen(true)} title="Creer automatiquement les taches dans le Planning" data-testid="forecast-generate-planning">
            <CalendarPlus size={12} weight="bold" /> Generer le planning
          </button>
        </div>
      </div>
      <table className="data-table" style={{ width: '100%', fontSize: '0.85rem' }}>
        <thead>
          <tr>
            <th>Salarie</th>
            <th style={{ textAlign: 'right', width: 110 }}>Taux horaire</th>
            <th style={{ textAlign: 'right', width: 130 }}>Heures previsionnelles</th>
            <th style={{ textAlign: 'right', width: 120 }}>Cout previsionnel HT</th>
            <th style={{ textAlign: 'right', width: 120 }}>Heures deja saisies</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.user_id} data-testid={`forecast-row-${r.user_id}`}>
              <td>
                <div style={{ fontWeight: 600 }}>{r.name}</div>
                {r.job_title && <div style={{ fontSize: '0.7rem', color: '#6B7280' }}>{r.job_title}</div>}
              </td>
              <td style={{ textAlign: 'right' }}>
                {r.hourly_rate > 0 ? fmtMoney(r.hourly_rate) : <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>non renseigne</span>}
              </td>
              <td style={{ textAlign: 'right' }}>
                <input
                  type="number" min="0" step="0.5"
                  className="form-input"
                  value={splitMap[r.user_id] ?? 0}
                  onChange={(e) => setSplitMap({ ...splitMap, [r.user_id]: parseFloat(e.target.value) || 0 })}
                  data-testid={`forecast-hours-${r.user_id}`}
                  style={{ padding: '4px 8px', textAlign: 'right', width: 100, marginLeft: 'auto', display: 'inline-block' }}
                />
              </td>
              <td style={{ textAlign: 'right', fontWeight: 700, color: r.hourly_rate > 0 ? '#002FA7' : '#9CA3AF' }}>
                {fmtMoney(r.forecast_cost)}
              </td>
              <td style={{ textAlign: 'right', color: '#5E5F66' }}>
                {fmtHours(r.hours_approved + r.hours_pending)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ backgroundColor: overflow ? '#FEF2F2' : '#F0F9FF' }}>
            <td colSpan={2} style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>
              Total reparti
              {overflow && (
                <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#C62828', fontWeight: 600 }} data-testid="forecast-mismatch">
                  ({remaining > 0 ? `+${fmtHours(remaining)} a repartir` : `${fmtHours(remaining)} en trop`})
                </span>
              )}
            </td>
            <td style={{ textAlign: 'right', fontWeight: 800, fontSize: '1rem', padding: '10px 12px' }} data-testid="forecast-total-hours">
              {fmtHours(totalForecastHours)} / {fmtHours(quota)}
            </td>
            <td style={{ textAlign: 'right', fontWeight: 800, fontSize: '1rem', padding: '10px 12px', color: '#002FA7' }} data-testid="forecast-total-cost">
              {fmtMoney(totalForecastCost)}
            </td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      <div style={{ marginTop: 8, fontSize: '0.72rem', color: '#9CA3AF', fontStyle: 'italic' }}>
        Taux horaire calcule depuis le taux journalier du salarie (1 jour = {HOURLY_DIVISOR}h).
      </div>

      {genOpen && (
        <GeneratePlanningModal
          rows={rows}
          project={project}
          onClose={() => setGenOpen(false)}
        />
      )}
    </div>
  );
};

// --- Generate planning tasks from the forecast ---
const GeneratePlanningModal = ({ rows, project, onClose }) => {
  const today = new Date();
  const fmtDate = (d) => d.toISOString().slice(0, 10);
  const todayStr = fmtDate(today);
  const in14days = new Date(today.getTime() + 14 * 24 * 3600 * 1000);
  const [form, setForm] = useState({
    start_date: todayStr,
    end_date: fmtDate(in14days),
    start_time: '08:00',
    end_time: '17:00',
    weekdays: [0, 1, 2, 3, 4], // Mon-Fri (Python convention: Mon=0..Sun=6)
    title: project ? `${project.name || 'Chantier'}` : 'Tache chantier',
    address: project?.address || '',
  });
  const [generating, setGenerating] = useState(false);

  const eligibleRows = (rows || []).filter(r => (r.forecast_hours || 0) > 0);

  const toggleWeekday = (i) => {
    setForm(f => ({ ...f, weekdays: f.weekdays.includes(i) ? f.weekdays.filter(w => w !== i) : [...f.weekdays, i].sort() }));
  };

  // Count working days in range
  const workingDaysCount = useMemo(() => {
    if (!form.start_date || !form.end_date) return 0;
    const s = new Date(form.start_date + 'T00:00:00');
    const e = new Date(form.end_date + 'T00:00:00');
    if (e < s) return 0;
    let n = 0;
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      // JS getDay(): Sun=0..Sat=6 -> convert to Mon=0..Sun=6
      const jsDay = d.getDay();
      const pyDay = (jsDay + 6) % 7;
      if (form.weekdays.includes(pyDay)) n++;
    }
    return n;
  }, [form.start_date, form.end_date, form.weekdays]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.weekdays.length === 0) { toast.error('Selectionnez au moins un jour'); return; }
    if (workingDaysCount === 0) { toast.error('Aucun jour ouvre dans la periode'); return; }
    if (eligibleRows.length === 0) { toast.error('Aucun salarie avec des heures > 0'); return; }
    setGenerating(true);
    try {
      // Strategy: one POST /api/planning per employee with their full date range,
      // adjusting daily working hours so total hours over the range matches their forecast.
      // Backend creates 1 task per matching weekday in [start_date, end_date].
      let created = 0;
      for (const r of eligibleRows) {
        const hoursPerDay = Math.max(1, Math.round((r.forecast_hours / workingDaysCount) * 10) / 10);
        // Compute end_time based on start + hoursPerDay (1h pause assumed)
        const [sh, sm] = form.start_time.split(':').map(Number);
        const totalMin = sh * 60 + sm + Math.round(hoursPerDay * 60) + 60; // +60 lunch break
        let eh = Math.floor(totalMin / 60);
        let em = totalMin % 60;
        if (eh > 23) { eh = 23; em = 59; }
        const computedEnd = `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
        const body = {
          user_ids: [r.user_id],
          start_date: form.start_date,
          end_date: form.end_date,
          weekdays: form.weekdays,
          start_time: form.start_time,
          end_time: computedEnd,
          title: form.title,
          description: `Genere depuis le previsionnel: ${r.forecast_hours.toFixed(1)}h (${hoursPerDay}h/jour x ${workingDaysCount}j)`,
          address: form.address,
          color: '#1565C0',
          priority: 'normal',
          project_id: project?.id || null,
        };
        const res = await axios.post(`${API_URL}/api/planning`, body, { withCredentials: true });
        created += res.data?.count || 0;
      }
      toast.success(`${created} tache(s) creee(s) dans le Planning`, {
        action: { label: 'Voir', onClick: () => { window.location.href = '/planning'; } },
      });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la generation');
    } finally { setGenerating(false); }
  };

  const dayLabels = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} data-testid="generate-planning-modal">
      <div style={{ background: 'white', borderRadius: 10, width: '95%', maxWidth: 560, padding: 22, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarPlus size={18} weight="bold" /> Generer le planning depuis le previsionnel
          </h3>
          <button onClick={onClose} disabled={generating} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#5E5F66', margin: '0 0 14px 0' }}>
          Les heures de chaque salarie ({eligibleRows.length} concernes) seront reparties sur les jours ouvres choisis.
        </p>
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="form-group">
              <label className="form-label">Du *</label>
              <input type="date" required className="form-input" value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="gen-start-date" />
            </div>
            <div className="form-group">
              <label className="form-label">Au *</label>
              <input type="date" required className="form-input" value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="gen-end-date" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Jours ouvres</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {dayLabels.map((lbl, i) => {
                const active = form.weekdays.includes(i);
                return (
                  <button key={i} type="button" onClick={() => toggleWeekday(i)}
                    data-testid={`gen-day-${i}`}
                    style={{
                      padding: '6px 12px', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600,
                      border: '1px solid', cursor: 'pointer',
                      borderColor: active ? '#002FA7' : '#D1D5DB',
                      backgroundColor: active ? '#002FA7' : 'white',
                      color: active ? 'white' : '#1F2937',
                    }}>{lbl}</button>
                );
              })}
            </div>
            <div style={{ marginTop: 6, fontSize: '0.72rem', color: '#6B7280' }}>
              Periode : <strong>{workingDaysCount}</strong> jour(s) ouvre(s)
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="form-group">
              <label className="form-label">Heure de debut</label>
              <input type="time" className="form-input" value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="gen-start-time" />
            </div>
            <div className="form-group">
              <label className="form-label">Heure de fin (max)</label>
              <input type="time" className="form-input" value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="gen-end-time" />
              <div style={{ fontSize: '0.7rem', color: '#9CA3AF', marginTop: 2 }}>Calculee automatiquement par salarie selon ses heures.</div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Titre des taches</label>
            <input type="text" className="form-input" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="gen-title" />
          </div>
          <div className="form-group">
            <label className="form-label">Adresse (optionnel)</label>
            <input type="text" className="form-input" value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Adresse du chantier" />
          </div>

          {eligibleRows.length > 0 && workingDaysCount > 0 && (
            <div style={{ padding: '10px 12px', backgroundColor: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 6, marginBottom: 12, fontSize: '0.78rem' }}>
              <strong>Apercu :</strong>
              <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
                {eligibleRows.map(r => (
                  <li key={r.user_id}>
                    {r.name} : {r.forecast_hours.toFixed(1)}h soit ~{(r.forecast_hours / workingDaysCount).toFixed(1)}h/jour x {workingDaysCount}j
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={generating}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={generating} data-testid="gen-submit">
              <CalendarPlus size={14} /> {generating ? 'Generation...' : 'Generer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
